import React from 'react'
import { useNavigate } from '@tanstack/react-router'
import { filter } from 'lodash/fp'
import { CheckCircleIcon } from 'lucide-react'

import { Operators } from '@/shared/components/query-builder'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { AlertGroupBy } from '@/shared/generated'
import { useOptimizationGroupId } from '@/shared/hooks/useOptimzationGroupId'
import { getFiltersQueryString } from '@/shared/tools/location'

import {
	usePricingAlertsAnnotationsQuery,
	usePricingAlertsQuery,
} from '../api/queries'
import { getPricingAlertsTree } from '../lib/helpers'
import { PricingAlerts } from './PricingAlerts'

export const PricingAlertsWidget = ({ currency }: { currency?: string }) => {
	const navigate = useNavigate()
	const optimizationGroupId = useOptimizationGroupId()
	const annotations = usePricingAlertsAnnotationsQuery()
	const pricingAlerts = usePricingAlertsQuery(
		{
			group_by: AlertGroupBy.OptimizationGroupId,
			total_by_group: true,
			currency,
			optimization_group_ids: [optimizationGroupId as string],
		},
		{
			select: (data) => ({
				...data,
				by_level: filter(
					{ optimization_group_id: optimizationGroupId },
					data.by_level
				),
				by_id: filter(
					{ optimization_group_id: optimizationGroupId },
					data.by_id
				),
			}),
		}
	)
	const alertsTree = getPricingAlertsTree({
		pricingAlerts: pricingAlerts.data,
		annotations: annotations.data,
	})
	const handleNavigate = (ids: string[]) =>
		navigate({
			to: `/p/og/${optimizationGroupId}/products${getFiltersQueryString([
				{
					name: 'alerts',
					operation: Operators.ANY_OF,
					value: ids,
				},
			])}`,
		})
	const isLoading = pricingAlerts.isLoading || annotations.isLoading
	const isError = pricingAlerts.isError || annotations.isError
	if (isLoading) {
		return (
			<div className='flex w-full flex-col justify-center space-y-2'>
				<Skeleton className='h-10 w-full' />
				<Skeleton className='h-10 w-full' />
				<Skeleton className='h-10 w-full' />
			</div>
		)
	}
	if (isError) {
		return null
	}

	return (
		<div className='w-full'>
			{!alertsTree?.length ? (
				<div className='flex items-center justify-center text-center text-success'>
					<CheckCircleIcon
						className='mr-2 fill-success'
						height={24}
						width={24}
					/>
					No alerts detected
				</div>
			) : (
				<PricingAlerts
					alertsOf='og'
					alertsTree={alertsTree}
					onNavigate={handleNavigate}
				/>
			)}
		</div>
	)
}
